import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import ai from "../../helpers/axios";

import ArchivedMatchCard from "./archived-match-card";
import "./match-pair-card.css";

const ArchivedMatchesContent = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const countPerPage = 25;

  const getData = async ({ loadSilently = false } = {}) => {
    if (!loadSilently) setLoading(true);

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const params = {
      page,
      countPerPage,
      sortBy: "createdAt",
      sortDirection: "desc",
    };

    const response = await ai
      .auth(token)
      .get(`/api/flightLegs/archivedMatches`, { params })
      .catch((error) => {
        console.error(error?.response?.data || error);
        Sentry.captureException(error);
        return null;
      });

    if (response) {
      setRows(response.data.data || []);
      setCount(response.data.count || 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
    document.title = "Archived Matches | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.ceil(count / countPerPage);
  const startItem = count === 0 ? 0 : (page - 1) * countPerPage + 1;
  const endItem = Math.min(page * countPerPage, count);

  return (
    <div className="match-pairs-page">
      <div className="match-pairs__header">
        <div className="match-pairs__header-row">
          <div>
            <div className="match-pairs__header-pretitle">Business</div>
            <h1 className="match-pairs__header-title">Archived Matches</h1>
          </div>
        </div>

        <div className="match-pairs__filter-bar">
          <Link to="/flight-matches" className="match-pairs__archived-link">
            &larr; Back to Flight Matches
          </Link>
          <div className="match-pairs__header-description">
            Archived PFP match pairs. Unarchive to allow them to match again.
          </div>
        </div>
      </div>

      {loading && (
        <div className="match-pair-card__loader">
          <div className="match-pair-card__loader-spinner" />
          Loading archived matches...
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="match-pair-card__empty">No archived matches.</div>
      )}

      {!loading && rows.length > 0 && (
        <div className="match-pairs__list">
          {rows.map((row) => (
            <ArchivedMatchCard
              key={row._id}
              row={row}
              onUnarchived={() => getData({ loadSilently: true })}
            />
          ))}
        </div>
      )}

      {!loading && count > 0 && (
        <div className="match-pairs__pagination">
          <div className="match-pairs__pagination-info">
            Showing {startItem}–{endItem} of {count} pairs
          </div>
          <div className="match-pairs__pagination-buttons">
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchivedMatchesContent;
